import { useEffect, useRef, useState } from 'react';

interface Meta {
  version: string;
  compat: number;
}

export interface VersionStatus {
  /** A newer build is deployed (breaking or not). */
  updateAvailable: boolean;
  /** A release marked breaking (compat bumped) happened since this session loaded. */
  breaking: boolean;
}

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const META_URL = `${process.env.PUBLIC_URL || ''}/meta.json`;

async function fetchMeta(): Promise<Meta | null> {
  try {
    const res = await fetch(`${META_URL}?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    if (typeof data?.version !== 'string' || typeof data?.compat !== 'number') return null;
    return data as Meta;
  } catch {
    return null;
  }
}

/**
 * Detects when a newer FE build has been deployed.
 * Captures the deployed meta.json as the session baseline on first poll, then
 * compares on each subsequent poll. Production-only (no effect in dev).
 */
export function useVersionCheck(): VersionStatus {
  const [status, setStatus] = useState<VersionStatus>({ updateAvailable: false, breaking: false });
  const baseline = useRef<Meta | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    let cancelled = false;

    const check = async () => {
      const meta = await fetchMeta();
      if (cancelled || !meta) return;
      if (!baseline.current) {
        baseline.current = meta;
        return;
      }
      const breaking = meta.compat > baseline.current.compat;
      const updateAvailable = breaking || meta.version !== baseline.current.version;
      setStatus((prev) =>
        prev.updateAvailable === updateAvailable && prev.breaking === breaking
          ? prev
          : { updateAvailable, breaking }
      );
    };

    check();
    const id = window.setInterval(check, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return status;
}
